/**
 * Mettre les gammes à jour d'après un tarif : la revue, côté écran (lot C2).
 *
 * Fonctions PURES, sans React : ce qui est coché d'office, ce qui est proposé, ce qui part vers le serveur, ce qu'on
 * dit à l'utilisateur. Règles posées avec Benjamin (2026-10-12) :
 *
 * - **compléments cochés d'office, reformulations non** (un complément ne fait qu'allonger un texte coupé ; une
 *   reformulation le remplace par autre chose, à regarder une par une) ;
 * - **un doute n'est jamais coché** : prix qui double ou tombe sous la moitié (même seuil que le rappel orange de
 *   l'éditeur), prix manquant ou inutilisable, ou aucun droit d'écrire les prix ;
 * - **une décision par référence**, valable pour toutes les lignes qui la portent ;
 * - le client n'envoie QUE ses décisions : le serveur recalcule tout et reste l'autorité.
 */
import type {
  BilanApplicationTarif,
  BilanGammeTarif,
  CodeRefusGamme,
  DecisionTarif,
  GammeARevoir,
  GroupeReferenceTarif,
  LigneDeGroupeTarif,
} from '../api/client';
import { RAPPORT_PRIX_SUSPECT } from './gammes-edition';
import { cleRecherche, jetonsDeRecherche } from './gammes-recherche';
import { erreurDateDEffet, lignesFr } from './tarifs';

export interface DroitsRevue {
  /** Écrire les prix (pricing:manage ET pricing:read) : sans lui, aucune case de prix. */
  readonly peutEcrirePrix: boolean;
}

/** Ce que l'utilisateur a décidé pour UNE référence. */
export interface Choix {
  readonly designation: boolean;
  readonly prix: boolean;
}

/** Les choix, par clé de référence. */
export type ChoixParCle = Readonly<Record<string, Choix>>;

export type ChampDecision = 'designation' | 'prix';

/** Un accord au singulier ou au pluriel : « 1 gamme », « 25 gammes ». */
const accord = (n: number, singulier: string, pluriel: string): string => `${n} ${n > 1 ? pluriel : singulier}`;

// ----------------------------------------------------------- ce qui est proposé

/** Le texte du tarif change quelque chose à la ligne : il la complète ou la reformule. */
export const designationProposee = (g: Pick<GroupeReferenceTarif, 'designation'>): boolean =>
  g.designation === 'complete' || g.designation === 'reformule';

/** Le prix du tarif change quelque chose : il diffère, ou la ligne n'en avait pas. */
export const prixProposable = (g: Pick<GroupeReferenceTarif, 'prix'>): boolean =>
  g.prix === 'different' || g.prix === 'manquant';

/**
 * Une variation de prix qui n'a pas l'air d'une mise à jour ordinaire : le prix double (ou plus), ou tombe sous la
 * moitié. Même seuil que le rappel orange de l'éditeur de gamme (`RAPPORT_PRIX_SUSPECT`).
 */
export function variationSuspecte(variation: number | null | undefined): boolean {
  if (variation === null || variation === undefined) return false;
  return variation >= (RAPPORT_PRIX_SUSPECT - 1) * 100 || variation <= (1 / RAPPORT_PRIX_SUSPECT - 1) * 100;
}

/** Une case existe pour ce champ de ce groupe : il y a quelque chose à proposer, et le droit de l'appliquer. */
export function caseDisponible(g: GroupeReferenceTarif, champ: ChampDecision, droits: DroitsRevue): boolean {
  return champ === 'designation' ? designationProposee(g) : droits.peutEcrirePrix && prixProposable(g);
}

/**
 * Les cases cochées d'office. Compléments : oui. Reformulations : non. Prix : coché quand il diffère, sauf doute
 * (variation suspecte, prix manquant ou inutilisable) et sauf sans le droit de les écrire. Jamais un doute coché.
 */
export function choixParDefaut(groupes: readonly GroupeReferenceTarif[], droits: DroitsRevue): ChoixParCle {
  const choix: Record<string, Choix> = {};
  for (const g of groupes) {
    choix[g.cle] = {
      designation: g.designation === 'complete',
      prix: droits.peutEcrirePrix && g.prix === 'different' && !variationSuspecte(g.variationMax),
    };
  }
  return choix;
}

/** Les lignes d'un groupe qu'une décision modifierait réellement (celles déjà identiques ne changent pas). */
function lignesTouchees(
  g: GroupeReferenceTarif,
  c: Choix | undefined,
  droits: DroitsRevue,
): { lignes: LigneDeGroupeTarif[]; designations: number; prix: number } {
  const designation = c?.designation === true && caseDisponible(g, 'designation', droits);
  const prix = c?.prix === true && caseDisponible(g, 'prix', droits);
  const lignes: LigneDeGroupeTarif[] = [];
  let designations = 0;
  let nbPrix = 0;
  for (const l of g.lignes) {
    const parTexte = designation && (l.designationCas === 'complete' || l.designationCas === 'reformule');
    const parPrix = prix && (l.prixCas === 'different' || l.prixCas === 'manquant');
    if (parTexte) designations += 1;
    if (parPrix) nbPrix += 1;
    if (parTexte || parPrix) lignes.push(l);
  }
  return { lignes, designations, prix: nbPrix };
}

// ------------------------------------------------------------- ce qui part

/** Les décisions envoyées au serveur : seulement les références où quelque chose est coché ET proposé. */
export function decisionsDe(
  groupes: readonly GroupeReferenceTarif[],
  choix: ChoixParCle,
  droits: DroitsRevue,
): DecisionTarif[] {
  const decisions: DecisionTarif[] = [];
  for (const g of groupes) {
    const c = choix[g.cle];
    if (!c) continue;
    const designation = c.designation && caseDisponible(g, 'designation', droits);
    const prix = c.prix && caseDisponible(g, 'prix', droits);
    if (designation || prix) decisions.push({ cle: g.cle, designation, prix });
  }
  return decisions;
}

export interface Comptes {
  /** Références où au moins une ligne change. */
  readonly references: number;
  /** Lignes distinctes modifiées (une ligne dont le texte ET le prix changent compte une fois). */
  readonly lignes: number;
  readonly gammes: number;
  readonly designations: number;
  readonly prix: number;
}

export function comptes(
  groupes: readonly GroupeReferenceTarif[],
  choix: ChoixParCle,
  droits: DroitsRevue,
): Comptes {
  const lignes = new Set<string>();
  const gammes = new Set<string>();
  let references = 0;
  let designations = 0;
  let prix = 0;
  for (const g of groupes) {
    const t = lignesTouchees(g, choix[g.cle], droits);
    if (t.lignes.length === 0) continue;
    references += 1;
    designations += t.designations;
    prix += t.prix;
    for (const l of t.lignes) {
      lignes.add(`${l.profileId}/${l.lineKey}`);
      gammes.add(l.profileId);
    }
  }
  return { references, lignes: lignes.size, gammes: gammes.size, designations, prix };
}

/**
 * Les gammes à envoyer : celles où au moins une ligne change pour ces choix, chacune avec la version en vigueur que
 * la revue a montrée (si elle a bougé depuis, le serveur refuse la gamme dans son bilan). Une gamme où rien ne change
 * n'est pas envoyée : elle ne ferait qu'un refus « rien à changer ».
 */
export function gammesAEnvoyer(
  groupes: readonly GroupeReferenceTarif[],
  choix: ChoixParCle,
  gammes: readonly GammeARevoir[],
  droits: DroitsRevue,
): { profileId: string; versionId: string }[] {
  const concernees = new Set<string>();
  for (const g of groupes) {
    for (const l of lignesTouchees(g, choix[g.cle], droits).lignes) concernees.add(l.profileId);
  }
  return gammes
    .filter((g) => concernees.has(g.profileId))
    .map((g) => ({ profileId: g.profileId, versionId: g.versionId }));
}

/**
 * La date d'effet saisie : un message, ou `null`. Elle ne doit pas être antérieure à la version en vigueur de l'une
 * des gammes concernées (la nouvelle version n'entrerait jamais en vigueur : le serveur refuse cette gamme).
 */
export function erreurDeLaDate(
  validFrom: string,
  gammesEnvoyees: readonly { profileId: string }[],
  gammes: readonly GammeARevoir[],
): string | null {
  const envoyees = new Set(gammesEnvoyees.map((g) => g.profileId));
  const plusRecente = gammes
    .filter((g) => envoyees.has(g.profileId))
    .map((g) => g.validFrom)
    .sort()
    .at(-1);
  return erreurDateDEffet(validFrom, plusRecente ?? null);
}

// ------------------------------------------------------------- filtres et cases

export type Filtre = 'tout' | 'aCompleter' | 'reformulees' | 'prix' | 'aucunChangement';

export const LIBELLE_FILTRE: Readonly<Record<Filtre, string>> = {
  tout: 'Tout',
  aCompleter: 'À compléter',
  reformulees: 'Reformulées',
  prix: 'Prix qui diffèrent',
  aucunChangement: 'Rien à changer',
};

function correspondAuFiltre(g: GroupeReferenceTarif, filtre: Filtre): boolean {
  switch (filtre) {
    case 'tout':
      return true;
    case 'aCompleter':
      return g.designation === 'complete';
    case 'reformulees':
      return g.designation === 'reformule';
    case 'prix':
      return prixProposable(g);
    case 'aucunChangement':
      return !designationProposee(g) && !prixProposable(g);
  }
}

/** Tout ce qu'on peut retrouver d'une référence : elle-même, son texte du tarif, sa famille, ses gammes et leurs textes. */
function entreesDeRecherche(g: GroupeReferenceTarif): string[] {
  const champs = [
    g.reference,
    g.description,
    g.famille ?? '',
    ...g.lignes.flatMap((l) => [l.gammeCode, l.gammeLabel, l.designation]),
  ];
  return champs.map(cleRecherche).filter((e) => e !== '');
}

/** Les références qui répondent au filtre ET à la saisie (chaque mot doit se retrouver quelque part). */
export function filtrer(groupes: readonly GroupeReferenceTarif[], filtre: Filtre, saisie: string): GroupeReferenceTarif[] {
  const jetons = jetonsDeRecherche(saisie);
  return groupes.filter((g) => {
    if (!correspondAuFiltre(g, filtre)) return false;
    if (jetons.length === 0) return true;
    const entrees = entreesDeRecherche(g);
    return jetons.every((j) => entrees.some((e) => e.includes(j)));
  });
}

/** Combien de références dans chaque filtre (indépendamment de la recherche) : pour les pastilles. */
export function nombreParFiltre(groupes: readonly GroupeReferenceTarif[]): Readonly<Record<Filtre, number>> {
  return {
    tout: groupes.length,
    aCompleter: groupes.filter((g) => correspondAuFiltre(g, 'aCompleter')).length,
    reformulees: groupes.filter((g) => correspondAuFiltre(g, 'reformulees')).length,
    prix: groupes.filter((g) => correspondAuFiltre(g, 'prix')).length,
    aucunChangement: groupes.filter((g) => correspondAuFiltre(g, 'aucunChangement')).length,
  };
}

/** Une case change : une copie des choix, jamais une modification en place. */
export function basculer(choix: ChoixParCle, cle: string, champ: ChampDecision, valeur: boolean): ChoixParCle {
  const actuel = choix[cle] ?? { designation: false, prix: false };
  return { ...choix, [cle]: { ...actuel, [champ]: valeur } };
}

/** Cocher (ou décocher) un champ pour les seules références VISIBLES où il y a quelque chose à proposer. */
export function cocherLesVisibles(
  choix: ChoixParCle,
  visibles: readonly GroupeReferenceTarif[],
  champ: ChampDecision,
  valeur: boolean,
  droits: DroitsRevue,
): ChoixParCle {
  const suivant: Record<string, Choix> = { ...choix };
  for (const g of visibles) {
    if (!caseDisponible(g, champ, droits)) continue;
    suivant[g.cle] = { ...(suivant[g.cle] ?? { designation: false, prix: false }), [champ]: valeur };
  }
  return suivant;
}

/** Le travail de l'utilisateur diffère-t-il de ce qui était coché d'office ? (pour garder son travail) */
export function choixModifies(initiaux: ChoixParCle, courants: ChoixParCle): boolean {
  const cles = new Set([...Object.keys(initiaux), ...Object.keys(courants)]);
  for (const cle of cles) {
    const a = initiaux[cle];
    const b = courants[cle];
    if ((a?.designation ?? false) !== (b?.designation ?? false)) return true;
    if ((a?.prix ?? false) !== (b?.prix ?? false)) return true;
  }
  return false;
}

// ------------------------------------------------------------------ le bilan

/** L'adresse de la page Gammes sur une gamme (et sur une version de cette gamme, le brouillon créé par exemple). */
export function lienVersGamme(profileId: string, versionId?: string): string {
  return `/gammes?gamme=${encodeURIComponent(profileId)}${versionId ? `&version=${encodeURIComponent(versionId)}` : ''}`;
}

/** Une gamme sans changement n'est pas un refus : le serveur la signale, l'écran la range à part. */
export const estRienAChanger = (g: Pick<BilanGammeTarif, 'refus'>): boolean => g.refus?.code === 'AUCUN_CHANGEMENT';

export const TITRE_REFUS: Readonly<Record<CodeRefusGamme, string>> = {
  INTROUVABLE: 'Gamme introuvable',
  ARCHIVEE: 'Gamme archivée',
  SANS_VERSION: 'Aucune version en vigueur',
  A_CHANGE: 'La gamme a changé depuis la revue',
  BROUILLON_OUVERT: 'Brouillon déjà ouvert',
  VERSION_A_VENIR: 'Version à venir',
  DATE_ANTERIEURE: 'Date d’effet trop ancienne',
  AUCUN_CHANGEMENT: 'Rien à changer',
  CONTENU_INVALIDE: 'Contenu refusé',
  FOURNISSEUR_ARCHIVE: 'Fournisseur archivé',
  CONFLIT: 'Création simultanée',
  ERREUR: 'Erreur imprévue',
};

/** La phrase qui annonce le résultat de la création des brouillons. */
export function messageBilan(bilan: Pick<BilanApplicationTarif, 'brouillons' | 'refus' | 'lignes' | 'designations' | 'prix'>): string {
  if (bilan.brouillons === 0) {
    return 'Aucun brouillon n’a été créé : voir, gamme par gamme, ce qui a été refusé.';
  }
  const parties = [`${accord(bilan.designations, 'désignation mise à jour', 'désignations mises à jour')}`];
  if (bilan.prix !== undefined && bilan.prix > 0) parties.push(accord(bilan.prix, 'prix mis à jour', 'prix mis à jour'));
  return (
    `${accord(bilan.brouillons, 'brouillon créé', 'brouillons créés')} (${lignesFr(bilan.lignes)} modifiée${bilan.lignes > 1 ? 's' : ''} : ${parties.join(', ')}). ` +
    'Rien ne change pour les machines tant qu’ils ne sont pas publiés.' +
    (bilan.refus > 0 ? ` ${accord(bilan.refus, 'gamme refusée', 'gammes refusées')}, détail ci-dessous.` : '')
  );
}

export { accord as accordFr };
